import { create } from 'zustand';
import type { CharacterDTO } from '@/network/RestApi';

interface CharState {
  current: CharacterDTO | null;
  hp: number;
  mp: number;
  maxHp: number;
  maxMp: number;
  exp: number;
  expToLevel: number;
  zen: number;
  diamond: number;
  str: number;
  agi: number;
  vit: number;
  ene: number;
  statPoints: number;
  skillPoints: number;
  setCurrent: (c: CharacterDTO | null) => void;
  setVitals: (p: { hp?: number; mp?: number; maxHp?: number; maxMp?: number }) => void;
  setCurrency: (p: { zen?: number; diamond?: number }) => void;
  setExp: (cur: number, toLevel: number) => void;
  setPosition: (p: { x: number; y: number; mapId?: number }) => void;
  setStats: (p: { str: number; agi: number; vit: number; ene: number; statPoints: number }) => void;
  setSkillPoints: (sp: number) => void;
  setStatPoints: (sp: number) => void;
  applyExpGain: (p: { exp: number; level: number; statPoints?: number; skillPoints?: number }) => void;
}

// Derive max HP/MP from class & level (PRD_DamageFormula §1.5 + base growth).
// Backend chưa gửi maxHp/maxMp, tạm tính client-side để HUD hiển thị thanh thực tế.
export function deriveMaxVitals(klass: CharacterDTO['class'], level: number) {
  const lv = Math.max(1, level || 1);
  const baseHp = klass === 'warrior' ? 120 : klass === 'archer' ? 100 : 80;
  const baseMp = klass === 'wizard' ? 200 : klass === 'archer' ? 150 : 120;
  const hpPerLv = klass === 'warrior' ? 18 : klass === 'archer' ? 14 : 10;
  const mpPerLv = klass === 'wizard' ? 25 : klass === 'archer' ? 18 : 14;
  return {
    maxHp: Math.round(baseHp + hpPerLv * (lv - 1)),
    maxMp: Math.round(baseMp + mpPerLv * (lv - 1)),
  };
}

export const useCharacterStore = create<CharState>((set) => ({
  current: null,
  hp: 0, mp: 0, maxHp: 1, maxMp: 1,
  exp: 0, expToLevel: 1,
  zen: 0, diamond: 0,
  str: 0, agi: 0, vit: 0, ene: 0,
  statPoints: 0, skillPoints: 0,
  setCurrent: (c) => {
    if (!c) {
      set({ current: null, hp: 0, mp: 0, maxHp: 1, maxMp: 1, str: 0, agi: 0, vit: 0, ene: 0, statPoints: 0, skillPoints: 0 });
      return;
    }
    const { maxHp, maxMp } = deriveMaxVitals(c.class, c.level);
    // Backend chưa trả MP/HP runtime — coi 0/undefined như "đầy" để HUD và mana gate hoạt động.
    const hp = c.hp && c.hp > 0 ? Math.min(c.hp, maxHp) : maxHp;
    const mp = c.mp && c.mp > 0 ? Math.min(c.mp, maxMp) : maxMp;
    const exp = c.experience ?? 0;
    const expToLevel = 100 * (c.level + 1) * (c.level + 1);
    set({
      current: c, hp, mp, maxHp, maxMp, exp, expToLevel,
      zen: c.zen ?? 0, diamond: c.diamond ?? 0,
      str: c.str ?? 0, agi: c.agi ?? 0, vit: c.vit ?? 0, ene: c.ene ?? 0,
      statPoints: c.statPoints ?? 0, skillPoints: c.skillPoints ?? 0,
    });
  },
  setVitals: (p) => set((s) => ({ ...s, ...p })),
  setCurrency: (p) => set((s) => ({ ...s, ...p })),
  setExp: (cur, toLevel) => set({ exp: cur, expToLevel: toLevel }),
  setPosition: ({ x, y, mapId }) => set((s) => ({
    current: s.current
      ? { ...s.current, posX: x, posY: y, mapId: mapId ?? s.current.mapId }
      : s.current,
  })),
  setStats: (p) => set((s) => ({
    str: p.str, agi: p.agi, vit: p.vit, ene: p.ene, statPoints: p.statPoints,
    current: s.current ? { ...s.current, ...p } : s.current,
  })),
  setSkillPoints: (sp) => set((s) => ({
    skillPoints: sp,
    current: s.current ? { ...s.current, skillPoints: sp } : s.current,
  })),
  setStatPoints: (sp) => set((s) => ({
    statPoints: sp,
    current: s.current ? { ...s.current, statPoints: sp } : s.current,
  })),
  applyExpGain: ({ exp, level, statPoints, skillPoints }) => set((s) => {
    const expToLevel = 100 * (level + 1) * (level + 1);
    const next: Partial<CharState> = { exp, expToLevel };
    if (typeof statPoints === 'number') next.statPoints = statPoints;
    if (typeof skillPoints === 'number') next.skillPoints = skillPoints;
    if (s.current) {
      const klass = s.current.class;
      const leveled = level !== s.current.level;
      const merged = {
        ...s.current,
        level,
        experience: exp,
        statPoints: typeof statPoints === 'number' ? statPoints : s.current.statPoints,
        skillPoints: typeof skillPoints === 'number' ? skillPoints : s.current.skillPoints,
      };
      next.current = merged;
      if (leveled) {
        const { maxHp, maxMp } = deriveMaxVitals(klass, level);
        next.maxHp = maxHp;
        next.maxMp = maxMp;
        next.hp = maxHp;
        next.mp = maxMp;
      }
    }
    return next as CharState;
  }),
}));
